import { z } from "zod";

// ---------------------------------------------------------------------------
// Path validators
// ---------------------------------------------------------------------------

/**
 * Defense in depth: paths flow into S3 keys and into sandbox mount paths. A
 * bad path could escape the workspace prefix or carry bytes no key may hold.
 * Both validators reject:
 *   - >1024 chars; any segment >255 chars (POSIX limit)
 *   - `..` traversal segments
 *   - leading slash, backslashes, null bytes
 *
 * `workspaceFilePathSchema` requires non-empty (file keys must point at a
 * real object); `workspaceFolderPathSchema` allows empty (= workspace root).
 */
const applyPathRules = <T extends z.ZodString>(s: T) =>
  s
    .max(1024, "path exceeds 1024 character limit")
    .refine((p) => !p.includes("\0"), "path contains null byte")
    .refine((p) => !p.includes("\\"), "path must not contain backslashes")
    .refine((p) => !p.startsWith("/"), "path must be relative (no leading slash)")
    .refine((p) => !p.includes("//"), "path must not contain consecutive slashes")
    .refine(
      (p) => p.split("/").every((segment) => segment !== ".."),
      "path traversal segments (..) are not allowed"
    )
    .refine(
      (p) => p.split("/").every((segment) => segment !== "."),
      "path must not contain '.' segments"
    )
    .refine(
      (p) => p.split("/").every((segment) => segment.length <= 255),
      "each path segment must be ≤ 255 characters"
    )
    // NFC-normalize so macOS (NFD) and Linux/S3 (raw bytes) inputs collapse
    // to the same canonical key — prevents phantom-duplicate listings.
    .transform((p) => p.normalize("NFC"));

export const workspaceFilePathSchema = applyPathRules(z.string().min(1, "path cannot be empty"));

export const workspaceFolderPathSchema = applyPathRules(z.string());

// ---------------------------------------------------------------------------
// Domain shapes
// ---------------------------------------------------------------------------

/**
 * Per-folder rollup for one top-level (depth-1) folder in a workspace.
 * Returned only when the caller opts in via `?include=folder-stats` on the
 * list endpoint — see `WorkspaceListIncludeSchema`. Derived server-side from
 * the same object listing that produces the workspace-root stats (no extra
 * round-trips), so it's a cheap add-on for callers that need to know *which*
 * folder changed without a recursive walk.
 */
export const WorkspaceFolderStatsSchema = z.object({
  /** Top-level folder name, no leading/trailing slash (depth-1 only). */
  path: z.string(),
  fileCount: z.number().int().nonnegative(),
  totalBytes: z.number().int().nonnegative(),
  /** ISO 8601 of the most recent file in the folder, or `null` if it holds no files. */
  lastModifiedAt: z.string().datetime().nullable()
});

export const WorkspaceStatsSchema = z.object({
  fileCount: z.number().int().nonnegative(),
  totalBytes: z.number().int().nonnegative(),
  lastModifiedAt: z.string().datetime().nullable(),
  /**
   * Per-top-level-folder breakdown. Present only when the list request opts in
   * with `?include=folder-stats`; omitted otherwise to keep the default
   * response small. Sorted by `path`.
   */
  folders: z.array(WorkspaceFolderStatsSchema).optional()
});

/**
 * What a workspace is backed by. Mirrors the Prisma enum `WorkspaceKind`
 * exactly — hand-rolled (rather than `z.nativeEnum($Enums.WorkspaceKind)`)
 * to avoid pulling Prisma's runtime into `@nexus/types`, which the frontend
 * consumes. DRIVE = the original S3-mirrored filesystem. CODE = a read-only
 * projection of a Vibe git project (Chapter 26) — see `vibeGitProjectId`.
 */
export const WorkspaceKindSchema = z.enum(["DRIVE", "CODE"]);
export type WorkspaceKindValue = z.infer<typeof WorkspaceKindSchema>;

export const WorkspaceItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  /** True for admin-configured workspaces shared across all organizations.
   *  The org UI badges these and hides org-side rename/delete (admin-managed). */
  isShared: z.boolean(),
  kind: WorkspaceKindSchema,
  /** Set only when `kind === "CODE"` — the git project this workspace projects. */
  vibeGitProjectId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export const WorkspaceSummarySchema = WorkspaceItemSchema.extend({
  stats: WorkspaceStatsSchema
});

export const WorkspaceFileEntrySchema = z.object({
  /** Path relative to the workspace root, no leading slash. */
  path: z.string(),
  size: z.number().int().nonnegative(),
  modifiedAt: z.string().datetime(),
  etag: z.string().optional()
});

export const WorkspaceFolderEntrySchema = z.object({
  /** Single path segment (no slashes). */
  name: z.string(),
  /**
   * Most-recent modification across the folder's descendants (the latest file
   * anywhere in its subtree). `null` for an empty folder; absent on the
   * live/WebDAV path, which doesn't compute it.
   */
  modifiedAt: z.string().datetime().nullable().optional(),
  /**
   * Summed size in bytes of all descendant files. `null` for an empty folder;
   * absent on the live/WebDAV path, which doesn't compute it.
   */
  size: z.number().nullable().optional()
});

/**
 * Where a git reference points: a Vibe git project, a branch/tag on it, and an
 * optional subdirectory within that ref.
 *
 * `path` is `""` for the repository root — the same "empty means root"
 * convention `workspaceFolderPathSchema` already uses, rather than a second
 * spelling (`null`, `"/"`) for the same idea.
 */
export const WorkspaceGitReferenceTargetSchema = z.object({
  gitProjectId: z.string().uuid(),
  /** Branch or tag name. Reused verbatim as the `ref` on the git-contents API. */
  refName: z.string().min(1, "refName is required").max(255),
  /** Subdirectory inside the ref, or `""` for the repository root. */
  path: workspaceFolderPathSchema
});

/**
 * A git reference as it appears in a folder listing — Chapter 26's third
 * workspace item type, alongside folders and files.
 *
 * Deliberately carries only what the LIST needs to draw a row. Resolving where
 * it points costs one read of the descriptor object, so making the listing
 * carry the target would turn one folder listing into N+1 storage reads for a
 * value most rows never need. `GetGitReference` resolves a single row on
 * demand instead.
 */
export const WorkspaceGitReferenceEntrySchema = z.object({
  /** Display name — the descriptor's basename with the marker suffix removed. */
  name: z.string(),
  /**
   * Workspace-relative path of the descriptor object itself, marker suffix
   * INCLUDED. This is the path every git-reference route addresses it by, so
   * the client never has to re-append the suffix and get it subtly wrong.
   */
  path: z.string(),
  modifiedAt: z.string().datetime()
});

export const WorkspaceListingSchema = z.object({
  folders: z.array(WorkspaceFolderEntrySchema),
  files: z.array(WorkspaceFileEntrySchema),
  /**
   * Git references in this folder.
   *
   * `.default([])` rather than `.optional()`, which is not a style choice —
   * it is the only shape that satisfies both constraints at once. Consumers
   * see a REQUIRED array, so nobody can forget to handle an absent one and
   * silently drop every reference (indistinguishable from a folder that has
   * none). And the generated client parses every response through this
   * schema, so a plain required field would THROW on a response from a
   * backend that predates it — taking the whole file browser down for the
   * length of a deploy window. The default absorbs that instead, and it is a
   * usable value of the right type rather than a null.
   */
  references: z.array(WorkspaceGitReferenceEntrySchema).default([]),
  hasMore: z.boolean(),
  nextToken: z.string().optional()
});

// ---------------------------------------------------------------------------
// Inferred TS types
// ---------------------------------------------------------------------------

export type WorkspaceStats = z.infer<typeof WorkspaceStatsSchema>;
export type WorkspaceFolderStats = z.infer<typeof WorkspaceFolderStatsSchema>;
export type WorkspaceFileEntry = z.infer<typeof WorkspaceFileEntrySchema>;
export type WorkspaceFolderEntry = z.infer<typeof WorkspaceFolderEntrySchema>;
export type WorkspaceGitReferenceTarget = z.infer<typeof WorkspaceGitReferenceTargetSchema>;
export type WorkspaceGitReferenceEntry = z.infer<typeof WorkspaceGitReferenceEntrySchema>;
export type WorkspaceListing = z.infer<typeof WorkspaceListingSchema>;
